import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { CoachingCategoryDropdown } from '../../../../../components/coachingCategoryDropdown/CoachingCategoryDropdown';
import { LoadingSpinner } from '../../../../../components/loadingSpinner/loadingspinner';
import { useSearchConversationQuery } from '../../conversationLogSlice';
import { SearchConversationRequest } from '../../types';
import { CoachingEntityTypes, CoachingTabs } from '../../../../../utils/typeConstants';
import { ROLES } from '../../../../../constants';
import { selectCurrentUserManagerLevel } from '../../../../../pages/login/authSlice';

interface ConversationLogModalHeaderProps {
    setViewState: (viewState: 'read' | 'edit' | 'new') => void;
    setConversationList: (conversationList: any[]) => void;
    isRecipient: boolean;
    receiverId: string;
    authorId: string;
    inputMonth?: number;
    inputYear?: number;
    onSaveClicked: (convIds: number[]) => void; // Update the function signature
    handleCoreValueId: (coreValueIds: number[]) => void; //
    via?: number;
    coachingEntityId?: number;
}

const ConversationLogModalHeader = (props: ConversationLogModalHeaderProps) => {
    const now = new Date();
    const maxDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const [search, setSearch] = useState('');
    const [conversationCategory, setConversationCategory] = useState(0);
    const userManagerLevel = useSelector(selectCurrentUserManagerLevel);
    const [conversationDate, setConversationDate] = useState<Date | null>(
        new Date(props?.inputYear ?? new Date().getFullYear(), (props?.inputMonth ?? 1) - 1)
    );

    const initParams = () => {
        // add keys only if value exists
        const params: SearchConversationRequest = {
            isRecipient: props?.isRecipient || false,
            empNo: props.receiverId,
        };

        if (props?.receiverId) {
            params.empNo = props.receiverId;
        }

        if (props?.authorId) {
            params.authorEmpNo = props.authorId;
        }

        if (conversationDate) {
            params.month = conversationDate.getMonth() + 1;
            params.year = conversationDate.getFullYear();
        }

        if (conversationCategory) {
            params.categoryId = conversationCategory;
        }

        if (search) {
            params.search = search;
        }
        return params;
    };
    const [searchParams, setSearchParams] = useState<SearchConversationRequest>(initParams());

    const { data: conversations, isFetching } = useSearchConversationQuery(searchParams);

    useEffect(() => {
        if (props?.via === CoachingTabs.CoachingInsights) {
            props.setConversationList(conversations?.data?.filter((item: any) => item.coaching_entity_id === props.coachingEntityId && item.coaching_entity_type_id === CoachingEntityTypes.CoachingInsights) || []);
        }
        else
            props.setConversationList(conversations?.data || []);
    }, [conversations]);

    useEffect(() => {
        if (search.trim().length === 0) {
            return;
        }
        if (search.length >= 4) setConversationDate(null);
    }, [search]);

    const handleReset = () => {
        setSearch('');
        setConversationDate(now);
        setConversationCategory(0);
        setSearchParams((prev: SearchConversationRequest) => {
            const newParams = { ...prev };
            if (newParams?.search) {
                delete newParams.search;
            }
            if (newParams?.categoryId) {
                delete newParams.categoryId;
            }

            return { ...newParams, month: now.getMonth() + 1, year: now.getFullYear() };
        });
    };
    useEffect(() => {
        if (conversations?.data) {
            const convIds: number[] = [];
            const coreValueIds: number[] = [];

            conversations.data.forEach((item: any) => {
                const currentConvId = item.coaching_conversation_id || 0;
                let currentCoreValueId = 0;

                if (item.core_value_abbreviation === 'Lead') {
                    currentCoreValueId = 1;
                } else if (item.core_value_abbreviation === 'Right Thing') {
                    currentCoreValueId = 2;
                } else if (item.core_value_abbreviation === 'Amazing') {
                    currentCoreValueId = 3;
                } else if (item.core_value_abbreviation === 'Understand') {
                    currentCoreValueId = 4;
                } else if (item.core_value_abbreviation === 'Act As One') {
                    currentCoreValueId = 5;
                } else if (item.core_value_abbreviation === 'Live Life') {
                    currentCoreValueId = 6;
                }

                convIds.push(currentConvId);
                coreValueIds.push(currentCoreValueId);
            });

            // Pass the arrays to the parent component after the loop
            props.onSaveClicked(convIds);
            props.handleCoreValueId(coreValueIds);
        }
    }, [conversations]);

    const handleSearch = () => {
        if (search.trim().length === 0 && conversationCategory === 0 && conversationDate === null) {
            toast.error('Please select at least one filter');
            return;
        }
        if (search.trim().length > 0 && search.trim().length < 4) {
            toast.error('Search must be at least 4 characters');
            return;
        }
        setSearchParams(initParams());
    };
    return (
        <>
            <div className="d-flex justify-content-between mb-2">
                <h3>Discussion Log</h3>
                {(props?.via !== CoachingTabs.CoachingInsights || !ROLES.Agents.includes(userManagerLevel)) && (
                    <div>
                        <Button
                            label="New Discussion Log"
                            icon="btnPlusIcon"
                            className="btn-primary me-2"
                            onClick={() => props.setViewState('new')}
                        />
                    </div>
                )}
            </div>

            <div className="col-12 mb-2">
                <div className="d-flex justify-content-start">
                    <div className="col-4 me-2">
                        <h6>Search</h6>
                        <span className="p-input-icon-left w-100">
                            <i className="pi pi-search" />
                            <InputText
                                placeholder="Search"
                                className="borderRadius8 w-100"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </span>
                    </div>
                    <div className="col-4 me-2">
                        <CoachingCategoryDropdown
                            category={conversationCategory}
                            setCategory={setConversationCategory}
                            fullWidth
                        />
                    </div>
                    <div className="col-4 ms-4">
                        <h6>Date</h6>
                        <Calendar
                            id="conversation-date"
                            value={conversationDate}
                            view="month"
                            dateFormat="M-yy"
                            onChange={(e: any) => {
                                setConversationDate(e.value);
                            }}
                            showIcon
                            className="custom-calendar"
                            maxDate={maxDate}
                        />
                    </div>
                </div>
            </div>

            <div className="d-flex justify-content-end mb-2">
                <Button
                    label="Reset Filters"
                    onClick={handleReset}
                    className="btn-secondary me-2"
                />
                <Button
                    label="Search"
                    onClick={handleSearch}
                    className="btn-primary"
                    // style={{ backgroundColor: '#3299ff', color: 'white' }}
                />
            </div>

            <div className="splitSection" />

            {isFetching ? (
                <div>
                    <LoadingSpinner />
                </div>
            ) : (
                ''
            )}
        </>
    );
};

export default ConversationLogModalHeader;
